import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Check, ShieldOff, SlidersHorizontal } from "lucide-react";

const STORAGE_KEY = "azimi_cookie_consent";

const defaultPrefs = { essential: true, analytics: false, marketing: false };

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  const persist = (value) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...value, timestamp: new Date().toISOString() })
    );
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => persist({ essential: true, analytics: true, marketing: true });
  const rejectNonEssential = () => persist({ essential: true, analytics: false, marketing: false });
  const savePreferences = () => persist(prefs);

  if (!visible) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6" data-testid="cookie-consent">
      <div className="mx-auto max-w-4xl overflow-hidden rounded-2xl border border-border bg-white shadow-2xl ring-1 ring-navy/5">
        {!showPrefs ? (
          <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center sm:p-7">
            <div className="flex items-start gap-4">
              <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy/5 text-navy sm:flex">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-serif text-lg text-navy">We value your privacy</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  We use essential cookies to make our website work and, with your consent,
                  analytics cookies to improve it. See our{" "}
                  <Link to="/cookie-policy" className="font-semibold text-navy underline decoration-gold/50 underline-offset-2 hover:text-gold-dark" data-testid="cookie-policy-link">
                    Cookie Policy
                  </Link>{" "}
                  for details.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2.5 sm:min-w-[220px]">
              <button onClick={acceptAll} className="inline-flex items-center justify-center gap-2 rounded-lg bg-gold px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-gold-dark" data-testid="cookie-accept-all">
                <Check className="h-4 w-4" /> Accept All Cookies
              </button>
              <div className="flex gap-2.5">
                <button onClick={() => setShowPrefs(true)} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-navy/20 px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy" data-testid="cookie-manage">
                  <SlidersHorizontal className="h-4 w-4" /> Manage
                </button>
                <button onClick={rejectNonEssential} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-navy/20 px-4 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy" data-testid="cookie-reject">
                  <ShieldOff className="h-4 w-4" /> Reject
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-7" data-testid="cookie-preferences">
            <div className="flex items-center justify-between">
              <h3 className="font-serif text-lg text-navy">Manage cookie preferences</h3>
              <button onClick={() => setShowPrefs(false)} className="text-muted-foreground hover:text-navy" aria-label="Close preferences">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-5 space-y-3">
              {[
                { key: "essential", title: "Strictly necessary", desc: "Required for the website to function. Always on.", locked: true },
                { key: "analytics", title: "Analytics", desc: "Help us understand how the website is used so we can improve it." },
                { key: "marketing", title: "Marketing", desc: "Used to make communications more relevant to you." },
              ].map((c) => (
                <label key={c.key} className={`flex items-start justify-between gap-4 rounded-xl border border-border p-4 ${c.locked ? "bg-secondary/50" : "bg-white"}`}>
                  <span>
                    <span className="block text-sm font-semibold text-charcoal">{c.title}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{c.desc}</span>
                  </span>
                  <input
                    type="checkbox"
                    disabled={c.locked}
                    checked={c.locked ? true : prefs[c.key]}
                    onChange={(e) => setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))}
                    className="mt-1 h-4 w-4 shrink-0 rounded border-border accent-navy disabled:opacity-60"
                    data-testid={`cookie-pref-${c.key}`}
                  />
                </label>
              ))}
            </div>
            <div className="mt-6 flex flex-col gap-2.5 sm:flex-row sm:justify-end">
              <button onClick={rejectNonEssential} className="rounded-lg border border-navy/20 px-5 py-2.5 text-sm font-semibold text-navy transition-colors hover:border-navy" data-testid="cookie-reject-2">
                Reject Non-Essential
              </button>
              <button onClick={savePreferences} className="rounded-lg bg-navy px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-navy-light" data-testid="cookie-save">
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
